import React, { useCallback, useEffect, useMemo, useState } from "react";

export interface UseRangeSliderReturn {
  minValue: number;
  maxValue: number;
  minQueryValue: string | undefined;
  maxQueryValue: string | undefined;
  minInputValue: string;
  maxInputValue: string;
  min: number;
  max: number;
  submitHandler(event: React.FormEvent<HTMLFormElement>): void;
  sliderChangeHandler(values: number[]): void;
  sliderAfterChangeHandler(): void;
  inputChangeHandlers: InputChangeHandlers;
  updateMax: UpdateValueFunction;
  updateMin: UpdateValueFunction;
}

type UpdateValueFunction = (value: string, updateAll: boolean) => void;

interface InputChangeHandlers {
  min(event: React.ChangeEvent<HTMLInputElement>): void;
  max(event: React.ChangeEvent<HTMLInputElement>): void;
}

interface RangeSliderArgs {
  defaultMin?: string;
  defaultMax?: string;
  onUpdate?: () => void;
  min: number;
  max: number;
}

const useRangeSlider = ({
  defaultMin,
  defaultMax,
  min,
  max,
  onUpdate,
}: RangeSliderArgs): UseRangeSliderReturn => {
  const [minValue, setMinValue] = useState<number>(min);
  const [maxValue, setMaxValue] = useState<number>(max);
  const [minInputValue, setInputMinValue] = useState<string>(String(min));
  const [maxInputValue, setInputMaxValue] = useState<string>(String(max));

  const sliderAfterChangeHandler = useCallback(() => {
    if (onUpdate) onUpdate();
  }, [onUpdate]);

  const submitHandler = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (onUpdate) onUpdate();
    },
    [onUpdate],
  );

  const roundedValue = useCallback(
    (value: number) => {
      return Math.min(Math.max(value, min), max);
    },
    [min, max],
  );

  const updateMin = useCallback(
    (value: string, updateAll = false) => {
      if (!value) {
        setInputMinValue("");

        return;
      }

      const roundedMinValue = roundedValue(parseInt(value));

      if (updateAll) {
        setMinValue(roundedMinValue);
      }
      setInputMinValue(String(roundedMinValue));
    },
    [setInputMinValue, setMinValue, roundedValue],
  );

  const updateMax = useCallback(
    (value: string, updateAll = false) => {
      if (!value) {
        setInputMaxValue("");

        return;
      }

      const roundedMaxValue = roundedValue(parseInt(value));

      if (updateAll) {
        setMaxValue(roundedMaxValue);
      }
      setInputMaxValue(String(roundedMaxValue));
    },
    [setInputMaxValue, setMaxValue, roundedValue],
  );

  const sliderChangeHandler = useCallback(
    (values: number[]) => {
      updateMin(String(values[0]), true);
      updateMax(String(values[1]), true);
    },
    [updateMin, updateMax],
  );

  const minInputChangeHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateMin(event.target.value);
    },
    [updateMin],
  );

  const maxInputChangeHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateMax(event.target.value);
    },
    [updateMax],
  );

  const minQueryValue = useMemo<string | undefined>(() => {
    if (!minInputValue || parseInt(minInputValue) <= min) {
      return undefined;
    }

    return parseInt(minInputValue) > parseInt(maxInputValue)
      ? maxInputValue
      : minInputValue;
  }, [minInputValue, maxInputValue, min]);

  const maxQueryValue = useMemo<string | undefined>(() => {
    if (!maxInputValue || parseInt(maxInputValue) >= max) {
      return undefined;
    }

    return parseInt(maxInputValue) < parseInt(minInputValue)
      ? minInputValue
      : maxInputValue;
  }, [minInputValue, maxInputValue, max]);

  useEffect(() => {
    updateMin(defaultMin ? defaultMin : String(min), true);
  }, [defaultMin, min, updateMin]);

  useEffect(() => {
    updateMax(defaultMax ? defaultMax : String(max), true);
  }, [defaultMax, max, updateMax]);

  return {
    minValue,
    maxValue,
    minQueryValue,
    maxQueryValue,
    minInputValue,
    maxInputValue,
    min,
    max,
    submitHandler,
    sliderChangeHandler,
    sliderAfterChangeHandler,
    inputChangeHandlers: {
      min: minInputChangeHandler,
      max: maxInputChangeHandler,
    },
    updateMax,
    updateMin,
  };
};

export default useRangeSlider;
